import { type ReactNode, useEffect, useState } from 'react';
import QRCode from 'qrcode';

const LETTERHEAD_URL = 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/letterhead.webp';

interface PrintableDocumentProps {
  documentTitle: string;
  patientName: string;
  practitionerName: string;
  practitionerTitle?: string;
  date: string;
  verificationUrl?: string;
  letterhead?: boolean;
  onReady?: () => void;
  children: ReactNode;
}

export default function PrintableDocument({
  documentTitle,
  patientName,
  practitionerName,
  practitionerTitle,
  date,
  verificationUrl,
  letterhead,
  onReady,
  children,
}: PrintableDocumentProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');

  useEffect(() => {
    const generateQR = async () => {
      try {
        const targetUrl = verificationUrl || window.location.origin;
        const dataUrl = await QRCode.toDataURL(targetUrl, {
          width: 120,
          margin: 1,
          color: { dark: '#000000', light: '#ffffff' },
        });
        setQrDataUrl(dataUrl);
        if (onReady) onReady();
      } catch {
        if (onReady) onReady();
      }
    };
    generateQR();
  }, [verificationUrl, onReady]);

  // Précharge le papier en-tête de la clinique pour qu'il soit prêt dès le premier print
  useEffect(() => {
    if (!letterhead) return;
    const img = new Image();
    img.src = LETTERHEAD_URL;
    if (typeof img.decode === 'function') {
      img.decode().catch(() => {});
    }
  }, [letterhead]);

  return (
    <div className={`print-doc${letterhead ? ' print-doc-letterhead' : ''}`} style={{ position: 'relative' }}>
      {/* Contenu */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* ===== META ===== */}
        <div className="print-doc-meta">
          <div>
            <span className="print-label">Patient :</span> {patientName}
          </div>
          <div>
            <span className="print-label">Prescripteur :</span> {practitionerName}
            {practitionerTitle ? ` (${practitionerTitle})` : ''}
          </div>
          <div>
            <span className="print-label">Date :</span> {date}
          </div>
        </div>

        {/* ===== TITLE ===== */}
        <div className="print-doc-title">
          <h2>{documentTitle}</h2>
          <div className="print-doc-title-line"></div>
        </div>

        {/* ===== CONTENT ===== */}
        <div className="print-doc-content">{children}</div>

        {/* ===== QR CODE AUTHENTICITE ===== */}
        {qrDataUrl && (
          <div style={{
            marginTop: '8mm',
            paddingTop: '3mm',
            display: 'flex',
            justifyContent: 'center',
          }}>
            <img
              src={qrDataUrl}
              alt="QR Code d'authenticité"
              style={{ width: '20mm', height: '20mm' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}